import React, { useEffect, useState } from 'react';
import { useLocation, Redirect } from 'react-router-dom';
import queryString from 'query-string';
import { EmptyOrLoadingState } from '@motivo/guanyin/src/components';
import { exchangeCode, isSessionValid, ApiError } from '../services';
import { login, logout } from '../auth';
import { consumeCallbackParams } from '../urlCallbackParams';
import { addAuthBreadcrumb, parseCallbackState } from '../auth-utils';
import throwWithoutBreaking from '@motivo/guanyin/src/utils/throwWithoutBreaking';
import config from '../config';

function getNavigationType() {
  const navigationEntry = window.performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return navigationEntry?.type || 'unknown';
}

function isInvalidGrantError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'AUTH_CODE_INVALID_OR_EXPIRED';
}

function parseSafeRedirectPath(stored: string | null): string {
  if (!stored) return '/';
  if (stored.startsWith('/') && !stored.startsWith('//')) return stored;
  return '/';
}

function peekSafeRedirectPath(): string {
  return parseSafeRedirectPath(localStorage.getItem('redirectAfterLogin'));
}

// Single-flighted so a StrictMode remount can't re-run the flow and race the
// first run's code exchange with the stale pre-login cookie.
let callbackFlowPromise: Promise<boolean> | null = null;

export default function Callback({
  fetchViewer,
  onFreshLogin,
}: {
  fetchViewer: () => Promise<unknown>;
  onFreshLogin?: () => void;
}) {
  const [isViewerFetched, setIsViewerFetched] = useState(false);
  const location = useLocation();

  useEffect(() => {
    callbackFlowPromise ??= (async (): Promise<boolean> => {
      const queryParams = queryString.parse(location.search);
      const { code, state: rawState } = consumeCallbackParams() ?? {};
      const parsedState = parseCallbackState(rawState);

      addAuthBreadcrumb('callback_mounted', {
        path: location.pathname,
        searchKeys: Object.keys(queryParams).sort(),
        hasCode: Boolean(code),
        hasStateParam: Boolean(rawState),
        hasParsedState: Boolean(parsedState),
        navigationType: getNavigationType(),
        referrer: document.referrer || null,
        historyLength: window.history.length,
      });

      if (code) {
        onFreshLogin?.();

        const isAdmin = !!queryParams.isAdmin || Boolean(parsedState?.isAdmin);

        addAuthBreadcrumb('callback_exchange_code_started', {
          isAdmin,
          codeLength: code.length,
          stateKeys: parsedState ? Object.keys(parsedState).sort() : [],
        });

        try {
          await exchangeCode(code, isAdmin, rawState);
          addAuthBreadcrumb('callback_exchange_code_succeeded', { isAdmin });
        } catch (error) {
          const isInvalidGrant = isInvalidGrantError(error);
          addAuthBreadcrumb('callback_exchange_code_failed', {
            isAdmin,
            isInvalidGrant,
            errorMessage: error instanceof Error ? error.message : String(error),
          });

          if (!isInvalidGrant) {
            throwWithoutBreaking(error);
          }

          const sessionValid = await isSessionValid();
          addAuthBreadcrumb('callback_session_status_result', { sessionValid });

          if (!sessionValid) {
            login({
              reason: isInvalidGrant
                ? 'callback-invalid-grant-recovery'
                : 'callback-exchange-failed-recovery',
              redirect: peekSafeRedirectPath(),
            });
            return false;
          }
        }

        if (isAdmin) {
          if (config.motivoProduct === 'aphrodite')
            localStorage.setItem(
              'motivo_user_id',
              (queryParams.motivoUserId as string) || parsedState?.motivoUserId,
            );
          if (config.motivoProduct === 'astraea')
            localStorage.setItem(
              'motivo_organization_admin_id',
              (queryParams.organizationAdminId as string) || parsedState?.organizationAdminId,
            );
        } else {
          localStorage.removeItem('motivo_user_id');
          localStorage.removeItem('motivo_organization_admin_id');
        }
      }

      try {
        await fetchViewer();
        addAuthBreadcrumb('callback_viewer_fetched', {
          path: location.pathname,
        });
        return true;
      } catch (error) {
        addAuthBreadcrumb('callback_viewer_fetch_failed', {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        throwWithoutBreaking(error);
        try {
          await logout();
        } catch {
          // If logout fails (e.g. session already cleared), redirect to home as fallback
          window.location.href = '/';
        }
        return false;
      }
    })();

    void callbackFlowPromise.then((didFetchViewer) => {
      if (didFetchViewer) {
        setIsViewerFetched(true);
      }
    });
  }, [config, location.pathname, location.search]);

  if (isViewerFetched) {
    const redirectPath = parseSafeRedirectPath(localStorage.getItem('redirectAfterLogin'));
    localStorage.removeItem('redirectAfterLogin');
    addAuthBreadcrumb('callback_redirecting_after_login', {
      redirectTarget: redirectPath,
    });
    return <Redirect to={redirectPath} />;
  }

  return <EmptyOrLoadingState loading />;
}
